import { useMemo, useState } from 'react';

// Read-only planogram (POG) renderer. Salvaged from the retired app-spaceplanning
// "Visualise" tab, rebuilt against CSS tokens (no Tailwind) and our data shape.
// We CONSUME retailer planograms — this viewer never edits facings or positions;
// it draws the parsed grid the API returns.
//
// Physical scale (Matthew Blake feedback): each bay renders at its true width
// (a full bay = 3 ft / 91.5 cm) and every product at its real occupied width, via
// a cm→px scale, positioned by its absolute X — so gaps and empty shelf space show
// as they really are, rather than the bay collapsing to content width.
//
// Input: positions[] = [{ bay, shelf, sequence, ean, productName, brand, psaBrand,
//   subCategory, segment, ownBrand, hFacings, widthCm (unit width), occWidthCm
//   (occupied block width), xPos (absolute cm) }].
// meta = { bayCount, shelfCount, widthCm, bayGeometry:[{bay,xOrigin,width}] }.

// Distinct, stable colours for the major AU snack manufacturers; anything else
// gets a deterministic hashed hue so competitor brands stay visually consistent.
const BRAND_COLORS = {
  majans: '#2563eb', bhuja: '#2563eb', infuzions: '#3b82f6',
  pepsico: '#dc2626', smith: '#dc2626', doritos: '#ef4444', 'red rock': '#f87171',
  kellanova: '#7c3aed', pringles: '#8b5cf6',
  snackbrands: '#059669', cc: '#10b981', cheezels: '#34d399', thins: '#6ee7b7',
  intersnack: '#d97706', nobby: '#f59e0b', tyrrells: '#fbbf24',
  snyder: '#0891b2', kettle: '#06b6d4', calbee: '#ec4899', cobs: '#14b8a6',
  'jack links': '#7c2d12', jacklinks: '#7c2d12', 'gold medal': '#b45309',
  woolworths: '#16a34a', foodco: '#16a34a', coles: '#e11d48',
};

function hashHue(s) {
  let h = 0;
  for (let i = 0; i < s.length; i += 1) h = s.charCodeAt(i) + ((h << 5) - h);
  return Math.abs(h % 360);
}

export function brandColor(brand) {
  const key = String(brand ?? '').toLowerCase();
  if (!key) return '#6b7280';
  for (const [k, c] of Object.entries(BRAND_COLORS)) if (key.includes(k)) return c;
  return `hsl(${hashHue(key)}, 55%, 48%)`;
}

const SEGMENT_PALETTE = ['#10b981', '#f59e0b', '#8b5cf6', '#ef4444', '#3b82f6',
  '#ec4899', '#f97316', '#14b8a6', '#6366f1', '#eab308', '#06b6d4', '#a855f7'];

// "Majan's" (with apostrophe) is how the PSA spells it — match the "majan" stem.
const isOwn = (p) => p.ownBrand === true || p.ownBrand === 1
  || /majan|bhuja|infuzion/i.test(p.psaBrand ?? '') || /majan|bhuja|infuzion/i.test(p.brand ?? '');

const displayBrand = (p) => p.brand || p.psaBrand || 'Unknown';

// Real occupied width of the block on shelf (cm). Prefer the parsed occupied width;
// fall back to unit width × facings, then a nominal 8 cm/facing.
const occOf = (p) => p.occWidthCm || ((p.widthCm ?? 0) * (p.hFacings || 1)) || (p.hFacings || 1) * 8;

// How many single units WIDE each facing is: pitch (occupied ÷ facings) ÷ unit width.
// 1 → merchandised as loose units; >1 → shelf-ready carton that many units across.
const unitsWideOf = (p) => {
  if (p.occWidthCm && p.widthCm && p.hFacings) {
    return Math.max(1, Math.round((p.occWidthCm / p.hFacings) / p.widthCm));
  }
  return 1;
};

// Diverging/sequential heat-map colour: hex lerp between two endpoints.
const mixHex = (a, b, t) => {
  const ch = (x, i) => parseInt(x.slice(i, i + 2), 16);
  const to = (x, y) => Math.round(x + (y - x) * t).toString(16).padStart(2, '0');
  return `#${to(ch(a, 1), ch(b, 1))}${to(ch(a, 3), ch(b, 3))}${to(ch(a, 5), ch(b, 5))}`;
};

export default function PlanogramViewer({ positions = [], meta = {}, perf = null, perfMeta = null }) {
  const [colorMode, setColorMode] = useState('brand');  // brand | ownbrand | segment | performance
  const [facingMode, setFacingMode] = useState('carton'); // carton | unit
  const [perfMetric, setPerfMetric] = useState('pspw'); // pspw | upspw | growth
  const [perfWindow, setPerfWindow] = useState('13');   // '13' | '52'
  const [pxPerCm, setPxPerCm] = useState(3);
  const [shelfHeight, setShelfHeight] = useState(78);
  const [showValues, setShowValues] = useState(true);
  const [highlightOwn, setHighlightOwn] = useState(true);
  const [hover, setHover] = useState(null); // { p, x, y }

  // Bucket positions into bays → shelves → sequence-ordered products.
  const bays = useMemo(() => {
    const byBay = new Map();
    for (const p of positions) {
      const b = p.bay ?? 1;
      if (!byBay.has(b)) byBay.set(b, new Map());
      const shelves = byBay.get(b);
      const s = p.shelf ?? 1;
      if (!shelves.has(s)) shelves.set(s, []);
      shelves.get(s).push(p);
    }
    return [...byBay.entries()].sort((a, b) => a[0] - b[0]).map(([bayNumber, shelves]) => ({
      bayNumber,
      shelves: [...shelves.entries()].sort((a, b) => a[0] - b[0]).map(([shelfNumber, prods]) => ({
        shelfNumber,
        products: prods.slice().sort((a, b) => (a.xPos ?? a.sequence ?? 0) - (b.xPos ?? b.sequence ?? 0)),
      })),
    }));
  }, [positions]);

  // Physical bay frames. Prefer the parsed BayGeometry; fall back to an even split
  // of the plan width, then a nominal 3-ft bay.
  const bayGeo = useMemo(() => new Map((meta.bayGeometry ?? []).map((g) => [g.bay, g])), [meta.bayGeometry]);
  const fallbackBayW = (meta.widthCm && meta.bayCount) ? meta.widthCm / meta.bayCount : 91.5;
  const bayWidthCm = (n) => bayGeo.get(n)?.width || fallbackBayW;
  const bayOriginCm = (n) => (bayGeo.get(n)?.xOrigin ?? (n - 1) * fallbackBayW);

  const segmentColor = useMemo(() => {
    const segs = [...new Set(positions.map((p) => p.segment).filter(Boolean))];
    const m = new Map();
    segs.forEach((s, i) => m.set(s, SEGMENT_PALETTE[i % SEGMENT_PALETTE.length]));
    return (seg) => m.get(seg) ?? '#9ca3af';
  }, [positions]);

  // ── Scan-velocity heat-map (Matthew #4) ──
  const perfEnabled = !!(perf && perf.size > 0);
  const perfBasis = perfMeta?.velocityBasis === 'per-week' ? 'per-week' : 'per-store-week';
  const perfVal = (p) => {
    const m = perf?.get(String(p.ean ?? '').trim());
    if (!m) return null;
    const k = perfMetric === 'growth' ? `g${perfWindow}` : perfMetric === 'upspw' ? `u${perfWindow}` : `p${perfWindow}`;
    return m[k] == null ? null : m[k];
  };
  const perfScale = useMemo(() => {
    if (colorMode !== 'performance' || !perfEnabled) return null;
    const vals = positions.map(perfVal).filter((v) => v != null).sort((a, b) => a - b);
    if (!vals.length) return null;
    const q = (t) => vals[Math.min(vals.length - 1, Math.floor(t * (vals.length - 1)))];
    if (perfMetric === 'growth') {
      return { kind: 'diverging', hi: Math.min(0.5, Math.max(Math.abs(q(0.05)), Math.abs(q(0.95)), 0.05)) };
    }
    return { kind: 'sequential', lo: q(0.05), hi: q(0.95) };
  }, [colorMode, perfMetric, perfWindow, positions, perf, perfEnabled]); // eslint-disable-line react-hooks/exhaustive-deps
  const perfColor = (v) => {
    if (v == null || !perfScale) return 'var(--border)';   // grey = no scan data / competitor
    if (perfScale.kind === 'diverging') {
      const t = Math.max(-1, Math.min(1, v / (perfScale.hi || 1)));
      return t >= 0 ? mixHex('#e5e7eb', '#16a34a', t) : mixHex('#e5e7eb', '#dc2626', -t);
    }
    const t = Math.max(0, Math.min(1, (v - perfScale.lo) / ((perfScale.hi - perfScale.lo) || 1)));
    return mixHex('#dbeafe', '#1e3a8a', t);
  };

  const colorOf = (p) => {
    if (colorMode === 'performance') return perfColor(perfVal(p));
    if (colorMode === 'ownbrand') return isOwn(p) ? '#2563eb' : '#94a3b8';
    if (colorMode === 'segment') return segmentColor(p.segment);
    return brandColor(displayBrand(p));
  };

  // Facing count shown depends on the toggle: carton fronts, or total units wide.
  const facingCount = (p) => (facingMode === 'unit' ? (p.hFacings || 1) * unitsWideOf(p) : (p.hFacings || 1));
  const facingSuffix = facingMode === 'unit' ? 'u' : 'F';

  if (!positions.length) {
    return <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: 24 }}>No positions to display.</div>;
  }

  const totalFacings = positions.reduce((a, p) => a + facingCount(p), 0);
  const btn = (active, disabled) => ({
    padding: '4px 10px', borderRadius: 999, cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 600,
    border: '1px solid var(--border-soft)', opacity: disabled ? 0.45 : 1,
    background: active ? 'var(--accent, #2563eb)' : 'var(--bg-card)',
    color: active ? '#fff' : 'var(--text-body)',
  });
  const metricLabel = perfMetric === 'growth' ? '$ growth vs PY'
    : perfMetric === 'upspw' ? (perfBasis === 'per-week' ? 'Units / wk' : 'UPSPW')
      : (perfBasis === 'per-week' ? '$ / wk' : '$PSPW');
  const fmtVal = (v) => (v == null ? '—'
    : perfMetric === 'growth' ? `${v >= 0 ? '+' : ''}${(v * 100).toFixed(1)}%`
      : perfMetric === 'upspw' ? v.toLocaleString(undefined, { maximumFractionDigits: 1 })
        : `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {/* Controls */}
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 14, fontSize: 12, color: 'var(--text-muted)' }}>
        <div style={{ display: 'flex', gap: 6 }}>
          {[['brand', 'Brand'], ['ownbrand', 'Majans vs Comp'], ['segment', 'Segment']].map(([k, label]) => (
            <button key={k} onClick={() => setColorMode(k)} style={btn(colorMode === k)}>{label}</button>
          ))}
          <button onClick={() => perfEnabled && setColorMode('performance')} disabled={!perfEnabled}
            title={perfEnabled ? 'Heat-map by scan velocity' : 'No scan data for this account yet'}
            style={btn(colorMode === 'performance', !perfEnabled)}>Performance</button>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span>Facings:</span>
          {[['carton', 'Cartons'], ['unit', 'Units wide']].map(([k, label]) => (
            <button key={k} onClick={() => setFacingMode(k)} style={btn(facingMode === k)}>{label}</button>
          ))}
        </div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
          <input type="checkbox" checked={showValues} onChange={(e) => setShowValues(e.target.checked)} /> Labels
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
          <input type="checkbox" checked={highlightOwn} onChange={(e) => setHighlightOwn(e.target.checked)} /> Highlight Majans
        </label>
        <span style={{ flex: 1 }} />
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>Zoom
          <input type="range" min="1.5" max="6" step="0.5" value={pxPerCm} onChange={(e) => setPxPerCm(+e.target.value)} />
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>Height
          <input type="range" min="48" max="140" value={shelfHeight} onChange={(e) => setShelfHeight(+e.target.value)} />
        </label>
        <span>{bays.length} bays · {positions.length} products · {totalFacings} {facingMode === 'unit' ? 'units wide' : 'facings'}</span>
      </div>

      {/* Performance heat-map controls + legend + data-period label */}
      {colorMode === 'performance' && (
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 12, fontSize: 12, color: 'var(--text-muted)' }}>
          <div style={{ display: 'flex', gap: 6 }}>
            {[['pspw', perfBasis === 'per-week' ? '$ / wk' : '$PSPW'], ['upspw', perfBasis === 'per-week' ? 'Units / wk' : 'UPSPW'], ['growth', '$ Growth']].map(([k, label]) => (
              <button key={k} onClick={() => setPerfMetric(k)} style={btn(perfMetric === k)}>{label}</button>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            {[['13', '13 wk'], ['52', '52 wk']].map(([k, label]) => (
              <button key={k} onClick={() => setPerfWindow(k)} style={btn(perfWindow === k)}>{label}</button>
            ))}
          </div>
          {perfScale && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {perfScale.kind === 'diverging' ? (<>
                <span>−{Math.round(perfScale.hi * 100)}%</span>
                <div style={{ width: 90, height: 10, borderRadius: 3, background: 'linear-gradient(90deg,#dc2626,#e5e7eb,#16a34a)' }} />
                <span>+{Math.round(perfScale.hi * 100)}%</span>
              </>) : (<>
                <span>{fmtVal(perfScale.lo)}</span>
                <div style={{ width: 90, height: 10, borderRadius: 3, background: 'linear-gradient(90deg,#dbeafe,#1e3a8a)' }} />
                <span>{fmtVal(perfScale.hi)}</span>
              </>)}
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                <span style={{ width: 10, height: 10, borderRadius: 2, background: 'var(--border)' }} /> no data
              </span>
            </div>
          )}
          <span style={{ flex: 1 }} />
          <span>{perfMeta?.accountName} scan · {metricLabel} · latest {perfWindow} wks to {perfMeta?.periodEndDate ? new Date(perfMeta.periodEndDate).toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}</span>
        </div>
      )}

      {/* Bays — each drawn at its true physical width (cm → px) */}
      <div style={{ display: 'flex', gap: 14, overflowX: 'auto', padding: '4px 4px 16px', background: 'var(--bg-card)', border: '1px solid var(--border-soft)', borderRadius: 'var(--radius, 8px)' }}
           onMouseLeave={() => setHover(null)}>
        {bays.map((bay) => {
          const wCm = bayWidthCm(bay.bayNumber);
          const originCm = bayOriginCm(bay.bayNumber);
          const bayPx = Math.max(60, wCm * pxPerCm);
          return (
            <div key={bay.bayNumber} style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: '0 0 auto' }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)', textAlign: 'center' }}>
                Bay {bay.bayNumber} · {Math.round(wCm)} cm
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, background: 'var(--border-soft)', padding: 6, borderRadius: 6 }}>
                {bay.shelves.map((shelf) => (
                  <div key={shelf.shelfNumber}
                       style={{ position: 'relative', width: bayPx, height: shelfHeight,
                                borderBottom: '3px solid var(--border)' }}>
                    {shelf.products.map((p) => {
                      const c = colorOf(p);
                      const own = isOwn(p);
                      const wPx = Math.max(5, occOf(p) * pxPerCm);
                      const leftPx = Math.max(0, ((p.xPos ?? 0) - originCm) * pxPerCm);
                      return (
                        <div key={p.positionId ?? `${p.ean}-${p.sequence}`}
                          onMouseMove={(e) => setHover({ p, x: e.clientX, y: e.clientY })}
                          onMouseLeave={() => setHover(null)}
                          style={{
                            position: 'absolute', left: leftPx, bottom: 0, width: wPx, height: shelfHeight - 3,
                            boxSizing: 'border-box', background: `${c}22`, borderLeft: `3px solid ${c}`,
                            borderRight: '1px solid var(--border-soft)', borderRadius: 2, overflow: 'hidden',
                            outline: highlightOwn && own ? '2px solid #2563eb' : 'none', outlineOffset: -2,
                          }}>
                          {highlightOwn && own && (
                            <div style={{ position: 'absolute', top: 1, left: 2, fontSize: wPx < 44 ? 8 : 10 }} title="Majans">★</div>
                          )}
                          <div style={{ position: 'absolute', top: 1, right: 2, fontSize: 8, fontWeight: 700,
                                        background: own ? '#dbeafe' : 'var(--border)', color: own ? '#1e40af' : 'var(--text-muted)',
                                        borderRadius: 2, padding: '0 3px' }}>{facingCount(p)}{facingSuffix}</div>
                          {showValues && wPx >= 34 && (
                            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column',
                                          alignItems: 'center', justifyContent: 'center', padding: '11px 2px 2px',
                                          textAlign: 'center', lineHeight: 1.1,
                                          fontSize: wPx < 50 ? 7 : wPx < 70 ? 8 : 9, color: 'var(--text-body)' }}>
                              {(p.productName ?? '').slice(0, wPx < 60 ? 16 : 28)}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Hover tooltip */}
      {hover && (
        <div style={{ position: 'fixed', left: Math.min(hover.x + 14, window.innerWidth - 260), top: hover.y + 14,
                      zIndex: 80, width: 240, background: 'var(--bg-card)', border: '1px solid var(--border)',
                      borderRadius: 6, boxShadow: '0 6px 20px rgba(0,0,0,0.18)', padding: 10, fontSize: 12,
                      color: 'var(--text-body)', pointerEvents: 'none' }}>
          <div style={{ fontWeight: 700, marginBottom: 4 }}>{hover.p.productName ?? '—'}</div>
          <div style={{ color: 'var(--text-muted)' }}>
            {[displayBrand(hover.p), hover.p.subCategory, hover.p.segment].filter(Boolean).join(' · ') || '—'}
          </div>
          <div style={{ color: 'var(--text-muted)', marginTop: 4 }}>
            {hover.p.hFacings || 1} facing{(hover.p.hFacings || 1) === 1 ? '' : 's'}
            {unitsWideOf(hover.p) > 1 ? ` × ${unitsWideOf(hover.p)} units wide` : ''}
            {hover.p.occWidthCm ? ` · ${Math.round(hover.p.occWidthCm)} cm on shelf` : ''}
          </div>
          <div style={{ color: 'var(--text-muted)', marginTop: 2 }}>
            EAN {hover.p.ean || '—'} · Bay {hover.p.bay} · Shelf {hover.p.shelf}
          </div>
          {perfEnabled && (() => {
            const m = perf.get(String(hover.p.ean ?? '').trim());
            const pl = perfBasis === 'per-week' ? '$/wk' : '$PSPW';
            const ul = perfBasis === 'per-week' ? 'u/wk' : 'UPSPW';
            const g = (x) => (x == null ? '—' : `${x >= 0 ? '+' : ''}${(x * 100).toFixed(1)}%`);
            const d = (x) => (x == null ? '—' : `$${x.toLocaleString(undefined, { maximumFractionDigits: 0 })}`);
            const n = (x) => (x == null ? '—' : x.toLocaleString(undefined, { maximumFractionDigits: 1 }));
            if (!m) return <div style={{ color: 'var(--text-muted)', marginTop: 4, fontSize: 11 }}>No {perfMeta?.accountName ?? 'account'} scan data</div>;
            return (
              <div style={{ marginTop: 4, paddingTop: 4, borderTop: '1px solid var(--border-soft)', fontSize: 11 }}>
                <div><b>13 wk</b> · {pl} {d(m.p13)} · {ul} {n(m.u13)} · Δ {g(m.g13)}</div>
                <div><b>52 wk</b> · {pl} {d(m.p52)} · {ul} {n(m.u52)} · Δ {g(m.g52)}</div>
              </div>
            );
          })()}
          {!hover.p.brand && hover.p.ean && (
            <div style={{ color: 'var(--warn-text)', marginTop: 4, fontSize: 11 }}>Not in Scan Item master</div>
          )}
        </div>
      )}
    </div>
  );
}
